// UI design tokens: muted palette, radii, shadows, typography
// Design tokens — light/dark ready, muted palette
// Theme variants with accessible contrast

export const LOCAL_ONLY_THEME_NAMES = new Set([
  'lightRedFocus',
]);

export const UI_THEME_KEYS = [
  'auroraExecutive',
  'auroraExecutiveLight',
  'redLightPremium',
  'lightRedFocus',
  'red2',
];

const RED_FOCUS_THEME_KEYS = new Set(['lightRedFocus']);

export function isRedFocusThemeName(name) {
  return RED_FOCUS_THEME_KEYS.has(String(name || ''));
}

export function isLocalOnlyThemeName(name) {
  return LOCAL_ONLY_THEME_NAMES.has(String(name || ''));
}

const NEON_DAY_BASE = {
  surface: '#ffffff',
  surface2: '#ecfeff',
  text: '#0f172a',
  mutedText: '#475569',
  border: '#cbd5e1',
  shadow: 'rgba(0,0,0,0.10)',
  primary: '#06b6d4',
  primarySoft: '#cffafe',
  danger: '#e11d48',
  warning: '#ea580c',
  success: '#16a34a',
};
export const themes = {
  light: {
    bg: '#f6f7f8',
    surface: '#ffffff',
    surface2: '#eef2f7',
    text: '#111827',
    mutedText: '#6b7280',
    border: '#d4d6db',
    shadow: 'rgba(0,0,0,0.10)',
    primary: '#2563eb',
    primarySoft: '#e0e7ff',
    danger: '#b91c1c',
    warning: '#b45309',
    success: '#15803d',
  },
  dark: {
    bg: '#0f1419',
    surface: '#131a21',
    surface2: '#0c1117',
    text: '#e5e7eb',
    mutedText: '#9aa4af',
    border: '#2b3642',
    shadow: 'rgba(0,0,0,0.40)',
    primary: '#60a5fa',
    primarySoft: '#172536',
    danger: '#ef4444',
    warning: '#f59e0b',
    success: '#34d399',
  },
  neonNight: {
    bg: '#0a0f1a',
    surface: '#0f1624',
    surface2: '#0b1220',
    text: '#e6f1ff',
    mutedText: '#9db4d1',
    border: '#21304a',
    shadow: 'rgba(0,0,0,0.45)',
    primary: '#8b5cf6',
    primarySoft: '#24123f',
    danger: '#ff4d6d',
    warning: '#fca311',
    success: '#2dd4bf',
  },
  neonDay: {
    bg: '#f8fafc',
    surface: '#ffffff',
    surface2: '#ecfeff',
    text: '#0f172a',
    mutedText: '#475569',
    border: '#cbd5e1',
    shadow: 'rgba(0,0,0,0.10)',
    primary: '#06b6d4',
    primarySoft: '#cffafe',
    danger: '#e11d48',
    warning: '#ea580c',
    success: '#16a34a',
  },

  // Premium executive dark theme
  auroraExecutive: {
    bg: '#08090D',
    surface: '#0B265D',
    surface2: '#111C2F',
    surfaceElevated: '#162A4A',
    text: '#F0F3F8',
    mutedText: '#A6B3C6',
    border: '#22324f',
    shadow: 'rgba(0,0,0,0.45)',
    primary: '#5063A8',
    primaryHover: '#4457A0',
    primarySoft: '#111C2F',
    // Magenta: selected/critical highlight only
    selected: '#731F55',
    highlightSoft: '#BD6A9E',
    accent: '#8FBFB9',
    danger: '#731F55',
    warning: '#BD6A9E',
    success: '#8FBFB9',
    focusRing: 'rgba(80,99,168,0.35)',
    cardRadius: 12,
    cardShadow: '0 6px 18px rgba(0,0,0,0.35)',
    cardShadowHover: '0 10px 28px rgba(0,0,0,0.45)',
    transitionFast: 'all 120ms ease',
    segmentedShadow: 'none',
    progressBg: '#22324f',
    chartGridOpacity: 0.12,
  },

  // Premium executive light theme (production)
  auroraExecutiveLight: {
    bg: '#F4F6F9',
    surface: '#FFFFFF',
    surface2: '#EEF1F6',
    text: '#1F2937',
    mutedText: '#6B7280',
    disabledText: '#9CA3AF',
    border: '#E5E9F0',
    shadow: 'rgba(0,0,0,0.04)',
    primary: '#5063A8',
    primaryHover: '#4457A0',
    primarySoft: 'rgba(80,99,168,0.08)',
    // Magenta: selected/critical highlight only
    selected: '#731F55',
    success: '#8FBFB9',
    danger: '#B71C1C',
    dangerHover: '#9A1717',
    focusRing: 'rgba(80,99,168,0.3)',
    cardRadius: 12,
    cardShadow: '0 4px 20px rgba(0,0,0,0.04)',
    cardShadowHover: '0 8px 24px rgba(0,0,0,0.06)',
    transitionFast: 'all 120ms ease',
    segmentedShadow: 'none',
    progressBg: '#E5E9F0',
    chartGridOpacity: 0.06,
  },

  // Professional light enterprise theme with controlled red accent (no gradients)
  redLightPremium: {
    bg: '#F6F7F9',
    surface: '#FFFFFF',
    surface2: '#F1F3F6',
    text: '#1E1E1E',
    mutedText: '#6B7280',
    disabledText: '#9CA3AF',
    border: '#E6E8EC',
    shadow: 'rgba(0,0,0,0.04)',
    primary: '#D72638',
    primaryHover: '#B71C2B',
    primarySoft: 'rgba(215,38,56,0.08)',
    selected: '#D72638',
    success: '#15803d',
    warning: '#b45309',
    danger: '#D72638',
    dangerHover: '#B71C2B',
    focusRing: 'rgba(215,38,56,0.25)',
    cardRadius: 12,
    cardShadow: '0 4px 20px rgba(0,0,0,0.04)',
    cardShadowHover: '0 8px 24px rgba(0,0,0,0.06)',
    transitionFast: 'all 120ms ease',
    segmentedShadow: 'none',
    progressBg: '#E6E8EC',
    chartGridOpacity: 0.06,
  },

  // Local-only preview themes
  lightRedFocus: {
    bg: 'linear-gradient(135deg, #ffe4e6 0%, #fff1f2 45%, #ffffff 100%)',
    ...NEON_DAY_BASE,
    primary: '#e11d48',
    primarySoft: '#fb7185',
    surface2: '#fff1f2',
  },

  // Copy of Red Prime with a cleaner white layout
  red2: {
    bg: '#FFFFFF',
    ...NEON_DAY_BASE,
    primary: '#e11d48',
    primarySoft: '#fb7185',
    surface: '#FFFFFF',
    surface2: '#fff1f2',
    border: '#cbd5e1',
    text: '#0f172a',
    mutedText: '#475569',
    shadow: 'rgba(0,0,0,0.10)',
  },
};

export function resolveThemeName() {
  try {
    const ls = localStorage.getItem('theme_preference');
    if (ls) {
      const raw = String(ls || '').trim();
      const migrated =
        raw === 'dark' ? 'auroraExecutive'
        : raw === 'light' ? 'auroraExecutiveLight'
        : raw === 'neonNight' ? 'auroraExecutive'
        : raw === 'neonDay' ? 'auroraExecutiveLight'
        : raw === 'neon_night' ? 'auroraExecutive'
        : raw === 'neon_day' ? 'auroraExecutiveLight'
        : raw === 'neonDayWarmFocus' ? 'auroraExecutiveLight'
        : raw === 'neonDayBlushMinimal' ? 'auroraExecutiveLight'
        : raw === 'neonDayPressureMode' ? 'auroraExecutiveLight'
        : raw;
      if (themes[migrated]) return migrated;
    }
  } catch {}
  // Fallback by DOM classes if present
  if (typeof document !== 'undefined') {
    const el = document.body || document.documentElement;
    if (el.classList.contains('theme-neon-night')) return 'auroraExecutive';
    if (el.classList.contains('theme-neon-day')) return 'auroraExecutiveLight';
    if (el.classList.contains('dark')) return 'auroraExecutive';
  }
  return 'auroraExecutiveLight';
}

export function getActiveTheme() {
  return themes[resolveThemeName()] || themes.light;
}

// Legacy aliases kept for minimal churn in existing imports
// Legacy aliases kept for minimal churn in existing imports (map to light theme)
export const colors = {
  surface: {
    bg: themes.light.bg,
    paper: themes.light.surface,
    inset: themes.light.surface2,
  },
  border: themes.light.border,
  text: {
    primary: themes.light.text,
    secondary: themes.light.mutedText,
    muted: themes.light.mutedText,
    inverse: '#ffffff',
  },
  status: {
    neutral: '#94a3b8',
    ok: '#7fb3a7',
    warning: '#b9a36b',
    over: '#b88383',
    done: '#a7f3d0',
  },
};

// Legacy design alias for existing imports (maps themes -> previous keys)
export const design = {
  light: {
    colors: {
      bgPage: themes.light.bg,
      bgCard: themes.light.surface,
      border: themes.light.border,
      textPrimary: themes.light.text,
      textMuted: themes.light.mutedText,
      semantic: {
        neutral: '#94a3b8',
        ok: '#7fb3a7',
        warning: '#b9a36b',
        danger: '#b88383',
      },
    },
  },
  dark: {
    colors: {
      bgPage: themes.dark.bg,
      bgCard: themes.dark.surface,
      border: themes.dark.border,
      textPrimary: themes.dark.text,
      textMuted: themes.dark.mutedText,
      semantic: {
        neutral: '#748090',
        ok: '#6aa699',
        warning: '#aa9561',
        danger: '#a56f6f',
      },
    },
  },
};

export const radii = {
  xs: 2,
  sm: 6,
  md: 10,
  lg: 14,
  xl: 18,
  round: 9999,
};

export const shadows = {
  sm: '0 1px 2px rgba(0,0,0,0.08)',
  md: '0 6px 16px rgba(0,0,0,0.15)',
  lg: '0 12px 24px rgba(0,0,0,0.18)',
};

export const spacing = {
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
};

export const typography = {
  h1: { fontSize: 24, fontWeight: 700, lineHeight: 1.25 },
  h2: { fontSize: 20, fontWeight: 600, lineHeight: 1.3 },
  h3: { fontSize: 16, fontWeight: 600, lineHeight: 1.35 },
  body: { fontSize: 14, fontWeight: 400, lineHeight: 1.5 },
  meta: { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
  // legacy sizes for minimal churn
  sizes: { xs: 12, sm: 14, md: 16, lg: 18, xl: 20 },
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
};

export default { themes, getActiveTheme, colors, radii, shadows, spacing, typography };
